import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { SERVICES } from '../config/assets';

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  // Get service-specific styling for the Services dropdown button
  const getServicesButtonStyle = () => {
    const path = location.pathname;
    if (path === '/services/polished-concrete') {
      return '!text-amber-600 dark:!text-amber-400 hover:!text-amber-700 dark:hover:!text-amber-300';
    } else if (path === '/services/grind-and-seal') {
      return '!text-blue-600 dark:!text-blue-400 hover:!text-blue-700 dark:hover:!text-blue-300';
    } else if (path === '/services/honed-concrete') {
      return '!text-emerald-600 dark:!text-emerald-400 hover:!text-emerald-700 dark:hover:!text-emerald-300';
    } else if (path === '/services/concrete-grinding-floor-prep') {
      return '!text-orange-600 dark:!text-orange-400 hover:!text-orange-700 dark:hover:!text-orange-300';
    } else if (path.startsWith('/services')) {
      return 'text-primary hover:text-primary/80';
    }
    return 'text-foreground hover:text-primary';
  };

  // Get service-specific hover colors for dropdown items
  const getServiceHoverStyle = (serviceSlug) => {
    switch (serviceSlug) {
      case 'polished-concrete':
        return 'hover:bg-amber-50 dark:hover:bg-amber-950 group-hover:!text-amber-600 dark:group-hover:!text-amber-400';
      case 'grind-and-seal':
        return 'hover:bg-blue-50 dark:hover:bg-blue-950 group-hover:!text-blue-600 dark:group-hover:!text-blue-400';
      case 'honed-concrete':
        return 'hover:bg-emerald-50 dark:hover:bg-emerald-950 group-hover:!text-emerald-600 dark:group-hover:!text-emerald-400';
      case 'concrete-grinding-floor-prep':
        return 'hover:bg-orange-50 dark:hover:bg-orange-950 group-hover:!text-orange-600 dark:group-hover:!text-orange-400';
      default:
        return 'hover:bg-accent group-hover:text-primary';
    }
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/aggregate-levels', label: 'Aggregate Levels' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/videos', label: 'YouTube' },
    { path: '/blog', label: 'Blog' },
    { path: '/contact', label: 'Contact' }
  ];

  const sectorLinks = [
    { path: '/sectors/commercial', label: 'Commercial', description: 'Office buildings, retail spaces, warehouses, and industrial facilities' },
    { path: '/sectors/residential', label: 'Residential', description: 'Homes, villas, apartments, and residential developments' }
  ];

  return (
    <header
      data-scrolled={scrolled}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 data-[scrolled=true]:bg-background data-[scrolled=true]:shadow-sm transition-all duration-200"
      data-testid="site-header"
    >
      <div className="container-custom h-16 flex items-center justify-between">
        <Link
          to="/"
          className="font-bold text-xl tracking-wide text-[hsl(42,47%,26%)] hover:text-primary transition-colors flex items-center gap-3"
          data-testid="brand-link"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_concretepro/artifacts/6xagib1k_logo.png"
            alt="Sydney Concrete Polishers Logo"
            className="h-8 w-auto max-w-[140px] object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <NavigationMenu className="hidden lg:flex">
          <NavigationMenuList className="flex items-center gap-6">
            {/* Home Link */}
            <NavigationMenuItem>
              <Link
                to="/"
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive('/') ? 'text-primary' : 'text-foreground'
                }`}
                data-testid="nav-home-link"
              >
                Home
              </Link>
            </NavigationMenuItem>

            {/* Services Dropdown */}
            <NavigationMenuItem>
              <NavigationMenuTrigger 
                className={`text-sm font-medium h-auto p-0 bg-transparent hover:bg-transparent focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent ${getServicesButtonStyle()}`}
                data-testid="nav-services-dropdown"
              >
                Services
              </NavigationMenuTrigger>
              <NavigationMenuContent className="min-w-[400px]">
                <div className="grid gap-3 p-6">
                  <h3 className="font-medium mb-2 text-foreground">Our Services</h3>
                  {SERVICES.map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className={`group block p-3 rounded-md transition-colors ${getServiceHoverStyle(service.slug)}`}
                      data-testid={`nav-service-${service.slug}-link`}
                    >
                      <div className="font-medium text-sm mb-1 text-foreground transition-colors">
                        {service.name}
                      </div>
                      <div className="text-xs text-muted-foreground leading-relaxed">
                        {service.description}
                      </div>
                    </Link>
                  ))}
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Sectors Dropdown */}
            <NavigationMenuItem>
              <NavigationMenuTrigger 
                className="text-sm font-medium h-auto p-0 bg-transparent hover:bg-transparent focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent"
                data-testid="nav-sectors-dropdown"
              >
                Sectors
              </NavigationMenuTrigger>
              <NavigationMenuContent className="min-w-[400px]">
                <div className="grid gap-3 p-6">
                  <h3 className="font-medium mb-2 text-foreground">Our Sectors</h3>
                  {sectorLinks.map((sector) => (
                    <Link
                      key={sector.path}
                      to={sector.path}
                      className="group block p-3 rounded-md hover:bg-accent transition-colors"
                      data-testid={`nav-sector-${sector.label.toLowerCase()}-link`}
                    >
                      <div className="font-medium text-sm mb-1 text-foreground group-hover:text-primary transition-colors">
                        {sector.label}
                      </div>
                      <div className="text-xs text-muted-foreground leading-relaxed">
                        {sector.description}
                      </div>
                    </Link>
                  ))}
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Other Navigation Links */}
            {navLinks.slice(1, -1).map((link) => (
              <NavigationMenuItem key={link.path}>
                <Link
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    isActive(link.path) ? 'text-primary' : 'text-foreground'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                >
                  {link.label}
                </Link>
              </NavigationMenuItem>
            ))}

            {/* Contact CTA */}
            <NavigationMenuItem>
              <Link to="/contact" data-testid="nav-contact-cta">
                <Button className="btn-primary" size="default">
                  Get a Quote
                </Button>
              </Link>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* Instagram Link */}
        <a
          href="https://www.instagram.com/sydneyconcretepolishers"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden lg:flex items-center gap-3 px-4 py-2 rounded-full text-white transition-all duration-300 shadow-md hover:shadow-lg"
          style={{
            background: 'linear-gradient(45deg, #405de6 0%, #833ab4 25%, #c13584 50%, #e1306c 75%, #fd1d1d 85%, #f56040 92%, #fcaf45 100%)',
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'linear-gradient(45deg, #3b4de0 0%, #732d99 25%, #a8296b 50%, #c72958 75%, #e01818 85%, #d4542e 92%, #e3a33d 100%)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'linear-gradient(45deg, #405de6 0%, #833ab4 25%, #c13584 50%, #e1306c 75%, #fd1d1d 85%, #f56040 92%, #fcaf45 100%)';
          }}
          data-testid="instagram-link"
          aria-label="Follow us on Instagram"
        >
          <span className="text-sm font-medium">Follow us on Instagram</span>
          <img 
            src="https://customer-assets.emergentagent.com/job_concretepro/artifacts/stjclcde_images.jpeg"
            alt="Instagram"
            className="w-5 h-5 rounded"
          />
        </a>

        {/* Mobile Instagram + Menu */}
        <div className="flex items-center gap-2 lg:hidden">
          <a
            href="https://www.instagram.com/sydneyconcretepolishers"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center w-10 h-10 rounded-full text-white transition-all duration-300 shadow-md hover:shadow-lg"
            style={{
              background: 'linear-gradient(45deg, #405de6 0%, #833ab4 25%, #c13584 50%, #e1306c 75%, #fd1d1d 85%, #f56040 92%, #fcaf45 100%)',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #3b4de0 0%, #732d99 25%, #a8296b 50%, #c72958 75%, #e01818 85%, #d4542e 92%, #e3a33d 100%)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(45deg, #405de6 0%, #833ab4 25%, #c13584 50%, #e1306c 75%, #fd1d1d 85%, #f56040 92%, #fcaf45 100%)';
            }}
            data-testid="mobile-instagram-link"
            aria-label="Follow us on Instagram"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_concretepro/artifacts/stjclcde_images.jpeg"
              alt="Instagram"
              className="w-5 h-5 rounded"
            />
          </a>
          
          {/* Mobile Navigation */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
          <SheetContent side="right" className="w-[280px]">
            <div className="flex flex-col gap-4 mt-8">
              {/* Home */}
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className={`text-base font-medium transition-colors hover:text-primary px-2 py-1 ${
                  isActive('/') ? 'text-primary' : 'text-foreground'
                }`}
                data-testid="mobile-nav-home-link"
              >
                Home
              </Link>

              {/* Services Section */}
              <div className="px-2 py-1">
                <div className="text-base font-medium text-muted-foreground mb-2">
                  Services
                </div>
                <div className="pl-4 flex flex-col gap-2">
                  {SERVICES.map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="text-sm font-medium transition-colors hover:text-primary py-1 text-foreground"
                      data-testid={`mobile-nav-service-${service.slug}-link`}
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Sectors Section */}
              <div className="px-2 py-1">
                <div className="text-base font-medium text-muted-foreground mb-2">
                  Sectors
                </div>
                <div className="pl-4 flex flex-col gap-2">
                  {sectorLinks.map((sector) => (
                    <Link
                      key={sector.path}
                      to={sector.path}
                      onClick={() => setMobileOpen(false)}
                      className="text-sm font-medium transition-colors hover:text-primary py-1 text-foreground"
                      data-testid={`mobile-nav-sector-${sector.label.toLowerCase()}-link`}
                    >
                      {sector.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Other Links */}
              {navLinks.slice(1).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={`text-base font-medium transition-colors hover:text-primary px-2 py-1 ${
                    isActive(link.path) ? 'text-primary' : 'text-foreground'
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </SheetContent>
        </Sheet>
        </div>
      </div>
    </header>
  );
};
